'use client'

import { ArrowLeft24, X24, Printer24, QrCode24 } from '@vinted/monochrome-icons'
import { Button, Cell, Dialog, Divider, Image, Navigation, Radio, Text, Icon } from '@vinted/web-ui'
import { useEffect, useCallback, useContext } from 'react'

import { GetShippingLabelContext } from '@marketplace-web/shipping-services-provider/shipping-services-provider-feature'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import {
  GenerateLabelModal,
  LabelType,
  viewEvent,
  clickEvent,
  LabelOptionsLabelType,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { UiState } from '@marketplace-web/shared/ui-state-util'

import {
  isReturnShipment,
  isPackageTypeLabelTypeFlow,
  getLabelTypesFromDropOffs,
  hasMultipleLabelTypesForSelectedDropOff,
  hasMultipleLabelTypesAcrossAllDropOffs,
} from '../../utils/generate-label'

type Props = {
  isOpen: boolean
}

const LabelTypeSelectionModal = ({ isOpen }: Props) => {
  const translate = useTranslate('conversation.get_shipping_label.select_label_type.modal')
  const commonTranslate = useTranslate('common.a11y.actions')
  const { track } = useTracking()
  const { conversation } = useConversationContext()

  const {
    state: {
      labelTypeSelection: {
        isLabelTypeSubmitEnabled,
        selectLabelType,
        clearLabelTypeSelection,
        selectedLabelType,
      },
      labelOptions: { labelOptions, labelOptionsUiState, labelOptionsRewrite },
      activeModal: { setActiveModal },
      dropOffTypeSelection: { clearDropOffTypeSelection, selectedDropOffTypeKey },
    },
  } = useContext(GetShippingLabelContext)

  const transactionId = conversation?.transaction?.id

  useEffect(() => {
    if (!isOpen || !transactionId) return

    const targetDetails = JSON.stringify({
      transaction_id: transactionId,
    })

    track(
      viewEvent({
        screen: 'label_type_selection',
        target: 'label_type_selection',
        targetDetails,
      }),
    )
  }, [track, transactionId, isOpen])

  const handleTracking = useCallback(
    (target, targetDetails) => {
      track(
        clickEvent({
          screen: 'label_type_selection',
          target,
          targetDetails: JSON.stringify(targetDetails),
        }),
      )
    },
    [track],
  )

  const getDigitalLabelType = () => {
    switch (selectedLabelType) {
      case LabelType.LabelTypePdf:
        return 'printable'
      case LabelType.LabelTypeQr:
        return 'digital'
      default:
        return null
    }
  }

  const renderCarrier = () => {
    const labelOptionsData = labelOptionsRewrite || labelOptions

    if (!labelOptionsData?.carrier) return null

    return (
      <>
        <Cell
          prefix={
            <Image
              src={labelOptionsData.carrier.iconUrl}
              size="large"
              styling="rounded"
              alt={labelOptionsData.carrier.name}
            />
          }
          title={labelOptionsData.carrier.name}
          body={!isReturnShipment(labelOptionsRewrite) && translate('carrier_body')}
        />
        <Divider />
      </>
    )
  }

  const renderTitle = () => <Cell body={<Text as="h1" type="heading" text={translate('body')} />} />

  const renderIcon = (icon: string | React.ComponentProps<typeof Icon>['name']) => {
    if (typeof icon !== 'string') {
      return <Icon name={icon} />
    }

    return <Image src={icon} styling="rounded" />
  }

  const renderLabelType = (
    type: LabelType | LabelOptionsLabelType,
    icon: string | React.ComponentProps<typeof Icon>['name'],
    title: string,
    subtitle: string,
  ) => (
    <Cell
      key={type}
      type="navigating"
      prefix={renderIcon(icon)}
      title={title}
      body={<Text as="span" html text={subtitle} />}
      onClick={() => selectLabelType(type)}
      divider
      suffix={
        <Radio
          name={`${type}-radio`}
          checked={type === selectedLabelType}
          value={type}
          testId={`label-type-type-selection-modal-${type}-radio`}
          onChange={() => undefined}
          aria={{ 'aria-labelledby': `label-type-type-selection-modal-${type}-radio` }}
        />
      }
    />
  )

  const renderRewriteLabelTypes = () => {
    if (!labelOptionsRewrite) return null

    const labelTypes = getLabelTypesFromDropOffs(labelOptionsRewrite, selectedDropOffTypeKey)

    const labelTypeConfig = {
      printable: {
        icon: Printer24,
        translateKey: 'printable_label',
        bodyKey: 'printable_label_body',
        backendCode: LabelOptionsLabelType.Printable,
      },
      digital: {
        icon: QrCode24,
        translateKey: 'digital_label',
        bodyKey: 'digital_label_body',
        backendCode: LabelOptionsLabelType.Digital,
      },
      code: {
        icon: QrCode24,
        translateKey: 'digital_label',
        bodyKey: 'digital_label_body',
        backendCode: LabelOptionsLabelType.Code,
      },
    }

    return labelTypes?.map(type => {
      const config = labelTypeConfig[type]

      return (
        config &&
        renderLabelType(
          config.backendCode,
          config.icon,
          translate(config.translateKey),
          translate(config.bodyKey),
        )
      )
    })
  }

  const renderLabelTypes = () =>
    labelOptions &&
    labelOptions.availableLabelTypes?.map(labelType =>
      renderLabelType(labelType.type, labelType.iconUrl, labelType.title, labelType.subtitle),
    )

  const handlePreviousClick = () => {
    if (isReturnShipment(labelOptionsRewrite)) {
      setActiveModal(GenerateLabelModal.ReturnTheOrder)
    } else {
      setActiveModal(GenerateLabelModal.DropOffTypeSelectionModal)
    }
  }

  const shouldShowBackButton = () => {
    if (isReturnShipment(labelOptionsRewrite)) return true

    if (labelOptionsRewrite) {
      const hasMultipleLabelTypes = hasMultipleLabelTypesAcrossAllDropOffs(labelOptionsRewrite)
      const hasMultipleForSelected = hasMultipleLabelTypesForSelectedDropOff(
        labelOptionsRewrite,
        selectedDropOffTypeKey,
      )

      return hasMultipleLabelTypes || hasMultipleForSelected
    }

    return false
  }

  const closeAndResetModal = () => {
    if (isPackageTypeLabelTypeFlow(labelOptionsRewrite)) {
      clearDropOffTypeSelection()
    }
    clearLabelTypeSelection()
    setActiveModal(GenerateLabelModal.None)
  }

  const handleSubmit = () => {
    handleTracking('select_label_type', {
      transaction_id: transactionId,
      label_type: getDigitalLabelType(),
    })

    const modal =
      isPackageTypeLabelTypeFlow(labelOptionsRewrite) && !isReturnShipment(labelOptionsRewrite)
        ? GenerateLabelModal.DropOffTypeSelectionModal
        : GenerateLabelModal.GetShippingLabelModal

    setActiveModal(modal)
  }

  const renderModalContent = () => {
    if (labelOptionsUiState === UiState.Pending) {
      return <ContentLoader testId="label-type-selection-modal-loader" />
    }

    return (
      <>
        {renderCarrier()}
        {renderTitle()}
        {renderLabelTypes()}
        {renderRewriteLabelTypes()}
        <div className="u-margin-top-auto">
          <Divider />
          <Cell>
            <Button
              styling="filled"
              text={translate('action.continue')}
              disabled={!isLabelTypeSubmitEnabled}
              onClick={handleSubmit}
              testId="label-type-type-selection-modal-submit-button"
            />
          </Cell>
        </div>
      </>
    )
  }

  return (
    <Dialog hasScrollableContent show={isOpen} testId="label-type-selection-modal">
      <div className="u-fill-width">
        <ScrollableArea>
          <Navigation
            body={translate('title')}
            left={
              shouldShowBackButton() && (
                <Button
                  styling="flat"
                  iconName={ArrowLeft24}
                  inline
                  onClick={handlePreviousClick}
                  testId="label-type-type-selection-modal-back-button"
                  aria={{ 'aria-label': commonTranslate('back') }}
                />
              )
            }
            right={
              <Button
                styling="flat"
                iconName={X24}
                inline
                onClick={closeAndResetModal}
                testId="close-selection-modal"
                aria={{ 'aria-label': commonTranslate('dialog_close') }}
              />
            }
          />
          <Divider />
          <div className="label-type-selection-modal__body">{renderModalContent()}</div>
        </ScrollableArea>
      </div>
    </Dialog>
  )
}

export default LabelTypeSelectionModal
